import type { FeeCategory } from "@netkeep/fee-types";
import { useEffect, useRef, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { AmountField } from "@/components/AmountField";
import { Breakdown } from "@/components/Breakdown";
import { FeeDataNotice } from "@/components/FeeDataNotice";
import { ProfilePicker } from "@/components/ProfilePicker";
import { ResultSummary } from "@/components/ResultSummary";
import { Button, Card, Muted, Section, SectionGroup, Segmented, Switch } from "@/components/ui";
import { describeRule } from "@/lib/describe-rule";
import { TAX_COLLECTOR_LABEL, money } from "@/lib/format";
import { track } from "@/lib/analytics";
import { saveCalculation } from "@/lib/saved";
import type { SeoPage } from "@/lib/seo";
import { useCalculator } from "@/state/calculator";
import { useFeeData } from "@/state/fee-data";

/** True when a form field holds a non-zero amount. */
const hasAmount = (v: string | undefined) => !!v && v.trim() !== "" && !/^\$?0*(\.0*)?$/.test(v.trim());

/** The calculator; on a platform page (`seo` set) it opens on that platform and shows its intro. */
export default function CalculatorPage({ seo }: { seo?: SeoPage }) {
  const { schedules } = useFeeData();
  const { form, profile, profileId, setProfileId, computation, reset } = useCalculator();
  const platformSlug = seo?.platformSlug;
  const preselected = useRef<string | null>(null);

  // Once per visit, so the user can still switch platforms on this page.
  useEffect(() => {
    if (!platformSlug || preselected.current === platformSlug) return;
    const match = schedules.find((s) => s.platform.slug === platformSlug);
    if (!match) return;
    preselected.current = platformSlug;
    setProfileId(match.id);
  }, [platformSlug, schedules, setProfileId]);
  const { control } = form;
  const errors = computation.fieldErrors;
  const values = useWatch({ control });
  const [savedNote, setSavedNote] = useState<{ text: string; error: boolean } | null>(null);

  useEffect(() => {
    if (!savedNote) return;
    const timer = setTimeout(() => setSavedNote(null), 2500);
    return () => clearTimeout(timer);
  }, [savedNote]);

  const hasPreset = (category: FeeCategory) => !!profile?.rules.some((r) => r.category === category);
  const origin = (category: FeeCategory, ...fields: (string | undefined)[]) =>
    fields.some((f) => f && f.trim() !== "") ? ("MANUAL" as const) : hasPreset(category) ? ("PROFILE" as const) : undefined;

  const marketplaceRules = profile?.rules.filter((r) => r.category === "MARKETPLACE") ?? [];
  const paymentRules = profile?.rules.filter((r) => r.category === "PAYMENT_PROCESSING") ?? [];
  const currency = profile?.currency ?? "USD";
  const result = computation.result;

  const extraCosts = [values.packagingCost, values.otherExpenses, values.subscriptionOverhead].filter(hasAmount).length;
  const manualFees = [
    values.marketplacePercent,
    values.marketplaceFixed,
    values.paymentPercent,
    values.paymentFixed,
    values.promotedListingPercent,
  ].filter(hasAmount).length;

  const onSelectProfile = (id: string) => {
    setProfileId(id);
    const chosen = schedules.find((s) => s.id === id);
    track(
      "profile_selected",
      chosen
        ? { platform: chosen.platform.slug, payment_method: chosen.paymentMethod.slug, transaction_type: chosen.transactionType }
        : { platform: "custom" },
    );
  };

  const onSave = () => {
    if (!result) return;
    const name = profile ? profile.platform.name : "Custom fees";
    try {
      saveCalculation({
        title: `${name} · ${money(result.revenue.itemPrice, currency)}`,
        profileId,
        profileName: profile ? `${profile.platform.name} — ${profile.paymentMethod.name}` : "Custom fees",
        values: form.getValues(),
        netProfit: result.netProfit,
        netPayout: result.netPayout,
        currency,
      });
      track("calculation_saved", { platform: profile?.platform.slug ?? "custom" });
      setSavedNote({ text: "Saved in this browser.", error: false });
    } catch (err) {
      setSavedNote({ text: err instanceof Error ? err.message : String(err), error: true });
    }
  };

  return (
    <>
      {seo?.heading ? (
        <header className="stack-xs">
          <h1 className="page-title">{seo.heading}</h1>
          {seo.intro ? <Muted>{seo.intro}</Muted> : null}
        </header>
      ) : (
        <h1 className="visually-hidden">Seller fee and profit calculator</h1>
      )}

      <ProfilePicker schedules={schedules} selectedId={profileId} onSelect={onSelectProfile}>
        <FeeDataNotice schedule={profile} />
      </ProfilePicker>

      <ResultSummary result={result} />
      {computation.error ? <p className="text-error">{computation.error}</p> : null}

      <form className="card" onSubmit={(e) => e.preventDefault()} aria-label="Sale details" noValidate>
        <div className="row row--wrap">
          <AmountField control={control} name="salePrice" label="Sale price" error={errors.salePrice} />
          <AmountField control={control} name="shippingCharged" label="Buyer pays shipping" error={errors.shippingCharged} />
        </div>
        <div className="row row--wrap">
          <AmountField control={control} name="itemCost" label="Item cost" error={errors.itemCost} />
          <AmountField control={control} name="shippingCost" label="Your shipping cost" error={errors.shippingCost} />
        </div>
      </form>

      <SectionGroup>
        <Section title="More costs" summary={extraCosts ? `${extraCosts} added` : "Packaging, other expenses, store fees"}>
          <div className="row row--wrap">
            <AmountField control={control} name="packagingCost" label="Packaging" error={errors.packagingCost} />
            <AmountField control={control} name="otherExpenses" label="Other expenses" error={errors.otherExpenses} />
          </div>
          <AmountField
            control={control}
            name="subscriptionOverhead"
            label="Store or subscription cost per sale"
            hint="E.g. monthly store fee ÷ sales per month"
            error={errors.subscriptionOverhead}
          />
        </Section>

        <Section
          title="Sales tax"
          summary={
            values.taxMode === "RATE"
              ? `${values.taxRate || "0"}% rate`
              : values.taxMode === "AMOUNT"
                ? money(values.taxAmount?.replace(/[$,\s]/g, "") || "0", currency)
                : "None"
          }
        >
          <Muted>
            Tax is never your revenue. It only matters when a platform charges fees on the total including tax.
            {profile ? ` ${TAX_COLLECTOR_LABEL[profile.taxCollectedBy]}.` : ""}
          </Muted>
          <Controller
            control={control}
            name="taxMode"
            render={({ field }) => (
              <Segmented
                label="Sales tax input mode"
                value={field.value}
                onChange={field.onChange}
                options={[
                  { value: "NONE", label: "None" },
                  { value: "RATE", label: "Rate %" },
                  { value: "AMOUNT", label: "Amount" },
                ]}
              />
            )}
          />
          {values.taxMode === "RATE" ? (
            <>
              <AmountField control={control} name="taxRate" label="Tax rate" kind="percent" error={errors.taxRate} />
              <Controller
                control={control}
                name="taxIncludesShipping"
                render={({ field }) => (
                  <Switch label="Tax applies to shipping too" checked={field.value} onChange={field.onChange} />
                )}
              />
            </>
          ) : null}
          {values.taxMode === "AMOUNT" ? (
            <AmountField control={control} name="taxAmount" label="Tax amount" error={errors.taxAmount} />
          ) : null}
        </Section>

        <Section
          title="Adjust fees"
          summary={manualFees ? `${manualFees} changed by you` : profile ? "Using preset rates" : "Enter your fees"}
        >
          {profile ? (
            <div className="stack-xs">
              {profile.rules.length === 0 ? <Muted>This profile has no platform fees.</Muted> : null}
              {profile.rules.map((r) => (
                <Muted key={r.code}>
                  {r.label}: {describeRule(r, currency)}
                </Muted>
              ))}
              <Muted className="faint">A value you type replaces the preset for that fee.</Muted>
            </div>
          ) : null}

          <h3 className="group-label">Marketplace fee</h3>
          <div className="row row--wrap">
            <AmountField
              control={control}
              name="marketplacePercent"
              label="Marketplace percent"
              kind="percent"
              placeholder={marketplaceRules.length ? "Preset" : "0"}
              origin={origin("MARKETPLACE", values.marketplacePercent, values.marketplaceFixed)}
              error={errors.marketplacePercent}
            />
            <AmountField
              control={control}
              name="marketplaceFixed"
              label="Marketplace fixed"
              placeholder={marketplaceRules.length ? "Preset" : "0.00"}
              error={errors.marketplaceFixed}
            />
          </div>

          <h3 className="group-label">Payment processing</h3>
          {profile?.paymentProcessingIncluded ? <Muted>Already included in this platform's fee.</Muted> : null}
          <div className="row row--wrap">
            <AmountField
              control={control}
              name="paymentPercent"
              label="Payment percent"
              kind="percent"
              placeholder={paymentRules.length ? "Preset" : "0"}
              origin={origin("PAYMENT_PROCESSING", values.paymentPercent, values.paymentFixed)}
              error={errors.paymentPercent}
            />
            <AmountField
              control={control}
              name="paymentFixed"
              label="Payment fixed"
              placeholder={paymentRules.length ? "Preset" : "0.00"}
              error={errors.paymentFixed}
            />
          </div>

          <AmountField
            control={control}
            name="promotedListingPercent"
            label="Promoted listing / ad rate"
            kind="percent"
            hint="Charged only if the promoted item sells"
            error={errors.promotedListingPercent}
          />
        </Section>

        {result ? (
          <Section title="Full breakdown" summary="Every fee and cost, line by line">
            <Breakdown result={result} embedded />
          </Section>
        ) : null}
      </SectionGroup>

      <Card>
        <div className="stack-xs">
          <Button title="Save calculation" onClick={onSave} disabled={!result} block />
          <Button title="Clear" variant="ghost" onClick={reset} block />
        </div>
        <p className={savedNote?.error ? "text-error center" : "text-accent center caption"} aria-live="polite">
          {savedNote?.text ?? ""}
        </p>
      </Card>
    </>
  );
}
